import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  RefreshCw, Trophy, Gift, Users, BadgeDollarSign, BarChart3,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, Tooltip, CartesianGrid, Bar, BarChart, YAxis, Legend,
} from "recharts";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";

const ACCENT = "#22c073";
const GOLD = "#d4af37";
const NAVY = "#0a1018";
const BLUE = "#3b82f6";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n) => Number(n || 0).toLocaleString("en-US");

export default function AffiliateAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState({ topAff: [], topPromo: [], regSeries: { series: [], total: 0 }, ftd: { series: [], total_count: 0, total_amount: 0 }, dep: { series: [], total: 0 } });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c, d, e] = await Promise.all([
        api.get("/admin/aff-analytics/top-affiliates", { params: { days, limit: 15 } }),
        api.get("/admin/aff-analytics/top-promo-codes", { params: { days, limit: 15 } }),
        api.get("/admin/aff-analytics/registration-stats", { params: { days } }),
        api.get("/admin/aff-analytics/ftd-stats", { params: { days } }),
        api.get("/admin/aff-analytics/deposit-stats", { params: { days } }),
      ]);
      setData({ topAff: a.data.rows || [], topPromo: b.data.rows || [], regSeries: c.data || {}, ftd: d.data || {}, dep: e.data || {} });
    } catch (e) { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  // Merge series into single multi-line chart
  const mergedSeries = (() => {
    const byDate = {};
    (data.regSeries.series || []).forEach((r) => { byDate[r.date] = { ...byDate[r.date], date: r.date, registrations: r.count }; });
    (data.ftd.series || []).forEach((r) => { byDate[r.date] = { ...byDate[r.date], date: r.date, ftd: r.count, ftd_amount: r.amount }; });
    (data.dep.series || []).forEach((r) => { byDate[r.date] = { ...byDate[r.date], date: r.date, deposits: r.amount, deposit_count: r.count }; });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  })();

  return (
    <div>
      <PageHeader title="Affiliate Analytics" subtitle="Top affiliates, top promo codes, registration / FTD / deposit trends — all in one place" />

      {/* Window picker */}
      <div className="rounded-xl p-3 mb-3 flex items-center gap-2" style={{ background: `linear-gradient(180deg, ${NAVY}d9, ${NAVY}f5)`, border: `1px solid ${ACCENT}3a` }}>
        <span className="text-[11px] uppercase tracking-widest text-white/55 font-bold mr-2">Window</span>
        {[7, 14, 30, 60, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} className="h-9 px-3 rounded-md text-[12px] font-bold" style={{
            background: days === d ? `${ACCENT}28` : "rgba(255,255,255,0.04)",
            border: `1px solid ${days === d ? ACCENT : "rgba(255,255,255,0.10)"}`,
            color: days === d ? ACCENT : "#cbd5e1",
          }} data-testid={`window-${d}`}>{d}d</button>
        ))}
        <button onClick={load} className="h-9 px-3 rounded-md text-[12px] inline-flex items-center gap-1.5 font-semibold ml-auto" style={{ background: `${ACCENT}1a`, border: `1px solid ${ACCENT}55`, color: ACCENT }}><RefreshCw size={12} /> Refresh</button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat label="Registrations" value={fmtInt(data.regSeries.total || 0)} c={GOLD} icon={Users} />
        <Stat label="FTD Count" value={fmtInt(data.ftd.total_count || 0)} c={ACCENT} icon={BadgeDollarSign} />
        <Stat label="FTD Volume" value={`৳ ${fmt(data.ftd.total_amount || 0)}`} c={ACCENT} icon={BadgeDollarSign} />
        <Stat label="Deposits" value={`৳ ${fmt(data.dep.total || 0)}`} c={BLUE} icon={BarChart3} />
      </div>

      {/* Trend chart */}
      <div className="rounded-xl p-4 mb-4 text-white" style={{ background: `linear-gradient(180deg, ${NAVY}d9, ${NAVY}f5)`, border: `1px solid ${ACCENT}3a` }}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold">Trends — last {days} days</div>
          <div className="text-[11px] text-white/55">registrations · FTD · deposits</div>
        </div>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <AreaChart data={mergedSeries} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gReg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={GOLD} stopOpacity={0.45} /><stop offset="100%" stopColor={GOLD} stopOpacity={0} /></linearGradient>
                <linearGradient id="gFtd" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={ACCENT} stopOpacity={0.5} /><stop offset="100%" stopColor={ACCENT} stopOpacity={0} /></linearGradient>
                <linearGradient id="gDep" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={BLUE} stopOpacity={0.35} /><stop offset="100%" stopColor={BLUE} stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} axisLine={false} interval={Math.max(0, Math.floor(mergedSeries.length / 8))} />
              <YAxis yAxisId="left" stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} axisLine={false} />
              <YAxis yAxisId="right" orientation="right" stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: NAVY, border: `1px solid ${ACCENT}55`, borderRadius: 10, color: "#f5f1e6", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#cbd5e1" }} />
              <Area yAxisId="left" type="monotone" dataKey="registrations" stroke={GOLD} strokeWidth={2} fill="url(#gReg)" name="Registrations" />
              <Area yAxisId="left" type="monotone" dataKey="ftd" stroke={ACCENT} strokeWidth={2} fill="url(#gFtd)" name="FTD" />
              <Area yAxisId="right" type="monotone" dataKey="deposits" stroke={BLUE} strokeWidth={2} fill="url(#gDep)" name="Deposits ৳" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Top affiliates */}
        <div className="rounded-xl p-4 text-white" style={{ background: `linear-gradient(180deg, ${NAVY}d9, ${NAVY}f5)`, border: `1px solid ${ACCENT}3a` }}>
          <div className="flex items-center gap-2 mb-3"><Trophy size={14} style={{ color: GOLD }} /><div className="text-sm font-bold">Top Affiliates · by commission</div></div>
          {data.topAff.length === 0 ? <div className="text-white/55 py-6 text-center text-[13px]">No data yet</div>
            : <div className="overflow-x-auto">
                <table className="w-full text-[13px] min-w-[420px]">
                  <thead><tr className="text-left text-[10.5px] uppercase tracking-widest" style={{ color: `${GOLD}cc` }}>
                    <th className="py-2 pr-2">#</th><th className="py-2 pr-2">Username</th><th className="py-2 pr-2">Code</th><th className="py-2 pr-2 text-right">Net Loss</th><th className="py-2 pr-2 text-right">Commission</th>
                  </tr></thead>
                  <tbody>
                    {data.topAff.map((r, i) => (
                      <tr key={r.affiliate_id || i} style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                        <td className="py-2 pr-2 text-white/55">{i + 1}</td>
                        <td className="py-2 pr-2 font-bold">{r.username || "—"}</td>
                        <td className="py-2 pr-2 font-mono" style={{ color: GOLD }}>{r.promo_code || "—"}</td>
                        <td className="py-2 pr-2 text-right">৳ {fmt(r.net_loss)}</td>
                        <td className="py-2 pr-2 text-right font-black" style={{ color: ACCENT }}>৳ {fmt(r.commission)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>}
        </div>

        {/* Top promo codes */}
        <div className="rounded-xl p-4 text-white" style={{ background: `linear-gradient(180deg, ${NAVY}d9, ${NAVY}f5)`, border: `1px solid ${ACCENT}3a` }}>
          <div className="flex items-center gap-2 mb-3"><Gift size={14} style={{ color: ACCENT }} /><div className="text-sm font-bold">Top Promo Codes · by deposits</div></div>
          {data.topPromo.length === 0 ? <div className="text-white/55 py-6 text-center text-[13px]">No data yet</div>
            : <div className="overflow-x-auto">
                <table className="w-full text-[13px] min-w-[480px]">
                  <thead><tr className="text-left text-[10.5px] uppercase tracking-widest" style={{ color: `${ACCENT}cc` }}>
                    <th className="py-2 pr-2">#</th><th className="py-2 pr-2">Code</th><th className="py-2 pr-2 text-right">Regs</th><th className="py-2 pr-2 text-right">FTD</th><th className="py-2 pr-2 text-right">FTD %</th><th className="py-2 pr-2 text-right">Deposits</th>
                  </tr></thead>
                  <tbody>
                    {data.topPromo.map((r, i) => (
                      <tr key={r.code || i} style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                        <td className="py-2 pr-2 text-white/55">{i + 1}</td>
                        <td className="py-2 pr-2 font-mono font-bold" style={{ color: GOLD }}>{r.code}</td>
                        <td className="py-2 pr-2 text-right">{r.registrations}</td>
                        <td className="py-2 pr-2 text-right" style={{ color: ACCENT }}>{r.ftd_count}</td>
                        <td className="py-2 pr-2 text-right">{r.ftd_rate_pct}%</td>
                        <td className="py-2 pr-2 text-right font-bold">৳ {fmt(r.deposits)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, c, icon: Icon }) {
  return (
    <div className="rounded-xl p-4" style={{
      background: `radial-gradient(400px 120px at 0% 0%, ${c}18, transparent 60%), linear-gradient(180deg, ${NAVY}d9, ${NAVY}f5)`,
      border: `1px solid ${c}3a`,
    }}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/55 font-bold">{label}</div>
        {Icon && <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${c}18`, border: `1px solid ${c}55`, color: c }}><Icon size={14} /></div>}
      </div>
      <div className="text-[22px] font-black" style={{ color: c }}>{value}</div>
    </div>
  );
}
